import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import {
  fetchQuery,
  getCacheEntry,
  getQueryData,
  isStale,
  subscribe,
} from '../api/queryCache';

/**
 * Subscribe to a cached query. Dedupes in-flight requests via queryCache.
 * @param {string} key
 * @param {() => Promise<any>} fetcher — stable reference from api/queries.js
 * @param {{ staleTime?: number, enabled?: boolean }} options
 */
export const useCachedQuery = (key, fetcher, { staleTime = 60_000, enabled = true } = {}) => {
  const getSnapshot = useCallback(() => getQueryData(key), [key]);

  const data = useSyncExternalStore(
    useCallback((cb) => subscribe(key, cb), [key]),
    getSnapshot,
    getSnapshot,
  );

  const [loading, setLoading] = useState(() => {
    if (!enabled) return false;
    const entry = getCacheEntry(key);
    return !entry || isStale(entry, staleTime);
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }

    const entry = getCacheEntry(key);
    const needsFetch = !entry || isStale(entry, staleTime);

    if (!needsFetch) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    if (!entry) setLoading(true);

    fetchQuery(key, fetcher, { staleTime })
      .then(() => {
        if (!cancelled) setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, fetcher, staleTime, enabled]);

  const refetch = useCallback(
    () => fetchQuery(key, fetcher, { staleTime, force: true }),
    [key, fetcher, staleTime],
  );

  return { data, loading, error, refetch };
};

export default useCachedQuery;
